import { useState, useMemo, useEffect } from 'react';
import { Icon, Input, Badge } from '@/components/atoms';
import { useProductAvailability } from '@/hooks/useErpApi';
import { erpClient } from '@/services/clients';
import type { ProductAvailabilityResponse } from '@kisanlink/erp-service';

interface ProductSearchProps {
  warehouseId: string;
  onProductSelect: (product: ProductAvailabilityResponse & { warehouse_id: string; warehouse_name: string; cost_price?: number }) => void;
}

/**
 * Flatten grouped availability structure to warehouse-specific items
 * for backward compatibility with existing component logic
 */
interface FlattenedProduct {
  sku: string;
  variant_id: string;
  product_name: string;
  product_sku: string;
  product_description?: string;
  warehouse_id: string;
  warehouse_name: string;
  total_quantity: number;
  expired_quantity: number;
  expiry_status: 'fresh' | 'expiring_soon' | 'expired';
  earliest_expiry: string;
  cost_price?: number; // Not available in grouped response, will be undefined
  prices?: any[]; // Preserve prices array from availability response
  images?: string[]; // NEW: S3 paths for variant images
  hsn_code?: string; // NEW: HSN code for GST
  gst_rate?: number; // NEW: Total GST rate
  cgst_rate?: number; // NEW: Central GST rate
  sgst_rate?: number; // NEW: State GST rate
}

function flattenAvailability(
  availability: ProductAvailabilityResponse[] | undefined,
  warehouseId?: string
): FlattenedProduct[] {
  if (!availability || availability.length === 0) return [];

  const flattened: FlattenedProduct[] = [];

  for (const product of availability) {
    // Type guard: Check if product has the new grouped structure
    const groupedProduct = product as any;
    if (!('warehouse_details' in groupedProduct) || !Array.isArray(groupedProduct.warehouse_details)) {
      // Old flat structure - skip or handle differently
      continue;
    }

    // Filter warehouse details by warehouseId if specified
    const warehouseDetails = warehouseId
      ? groupedProduct.warehouse_details.filter((wh: any) => wh.warehouse_id === warehouseId)
      : groupedProduct.warehouse_details;

    // Create a flattened item for each warehouse
    for (const warehouse of warehouseDetails) {
      flattened.push({
        sku: groupedProduct.sku,
        variant_id: groupedProduct.variant_id,
        product_name: groupedProduct.product_name,
        product_sku: groupedProduct.sku,
        product_description: groupedProduct.product_description,
        warehouse_id: warehouse.warehouse_id,
        warehouse_name: warehouse.warehouse_name,
        total_quantity: warehouse.quantity, // Available quantity in this warehouse
        expired_quantity: warehouse.expired_quantity,
        expiry_status: warehouse.expiry_status,
        earliest_expiry: warehouse.earliest_expiry,
        // Preserve prices array from the grouped product
        prices: groupedProduct.prices || [],
        // NEW: Preserve images and GST information
        images: groupedProduct.images || [],
        hsn_code: groupedProduct.hsn_code,
        gst_rate: groupedProduct.gst_rate,
        cgst_rate: groupedProduct.cgst_rate,
        sgst_rate: groupedProduct.sgst_rate,
        // cost_price is not available in grouped response
        // Will need to be fetched separately if needed
      });
    }
  }

  return flattened;
}

export const ProductSearch: React.FC<ProductSearchProps> = ({ warehouseId, onProductSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const { data: availability, isLoading } = useProductAvailability();

  // Fetch presigned URLs for variant images
  useEffect(() => {
    if (!availability) return;

    const fetchImageUrls = async () => {
      const urlMap: Record<string, string> = {};
      const variantsToFetch = new Set<string>();

      // Collect unique variants with images
      availability.forEach((product: any) => {
        if (product.images && product.images.length > 0 && product.variant_id) {
          variantsToFetch.add(product.variant_id);
        }
      });

      // Fetch presigned URLs for each variant (limit to top 10 to avoid too many API calls)
      const variantsArray = Array.from(variantsToFetch).slice(0, 10);
      await Promise.all(
        variantsArray.map(async (variantId) => {
          try {
            const attachments = await erpClient.attachments.getByEntity('variant', variantId);
            if (attachments.data && attachments.data.length > 0) {
              // Get presigned URL for the first attachment
              const attachment = attachments.data[0];
              const urlResponse = await erpClient.attachments.getDownloadUrl(attachment.id);
              if (urlResponse.data && (urlResponse.data as any).url) {
                urlMap[variantId] = (urlResponse.data as any).url;
              }
            }
          } catch (error) {
            console.warn(`Failed to get image URL for variant ${variantId}:`, error);
          }
        })
      );

      setImageUrls(urlMap);
    };

    fetchImageUrls();
  }, [availability]);

  // Flatten grouped structure to warehouse-specific items
  const flattenedProducts = useMemo(() => {
    return flattenAvailability(availability, warehouseId);
  }, [availability, warehouseId]);

  // Filter products by search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return flattenedProducts.slice(0, 10);

    const query = searchQuery.toLowerCase();
    return flattenedProducts
      .filter(
        (p) =>
          p.product_name.toLowerCase().includes(query) ||
          p.product_sku.toLowerCase().includes(query) ||
          p.sku.toLowerCase().includes(query) ||
          p.variant_id.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [flattenedProducts, searchQuery]);

  // Top products for quick access (products with highest stock)
  const topProducts = useMemo(() => {
    return flattenedProducts
      .filter((p) => p.total_quantity > 0)
      .sort((a, b) => b.total_quantity - a.total_quantity)
      .slice(0, 10);
  }, [flattenedProducts]);

  // Get expiry badge variant
  const getExpiryBadgeVariant = (status: 'fresh' | 'expiring_soon' | 'expired') => {
    switch (status) {
      case 'fresh':
        return 'success';
      case 'expiring_soon':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'secondary';
    }
  };

  // Handle product selection
  const handleSelectProduct = (product: FlattenedProduct) => {
    // Convert flattened product back to format expected by onProductSelect
    // We need to find the original grouped product to pass complete data
    const groupedProduct = availability?.find((p: any) => 'sku' in p && p.sku === product.sku && p.variant_id === product.variant_id);
    if (groupedProduct) {
      onProductSelect({
        ...groupedProduct,
        warehouse_id: product.warehouse_id,
        warehouse_name: product.warehouse_name,
        cost_price: product.cost_price,
      } as any);
    }
    setSearchQuery('');
    setShowDropdown(false);
    setSelectedIndex(0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filteredProducts.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredProducts.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredProducts.length) % filteredProducts.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredProducts[selectedIndex]) {
          handleSelectProduct(filteredProducts[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };


  return (
    <div className="space-y-6">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <Icon
            name="Search"
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
          />
          <Input
            placeholder="Search products by name, SKU, or variant ID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
              setSelectedIndex(0);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            className="pl-10 pr-4 py-3 text-lg"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            </div>
          )}
        </div>

        {/* Autocomplete Dropdown */}
        {showDropdown && searchQuery && filteredProducts.length > 0 && (
          <div className="absolute z-10 w-full mt-2 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {filteredProducts.map((product, index) => (
              <button
                key={`${product.variant_id}-${product.warehouse_id}`}
                onClick={() => handleSelectProduct(product)}
                className={`w-full px-4 py-3 text-left hover:bg-primary-50 transition-colors border-b border-neutral-100 last:border-b-0 ${
                  index === selectedIndex ? 'bg-primary-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-neutral-900">{product.product_name}</p>
                      <Badge variant={getExpiryBadgeVariant(product.expiry_status) as any} size="sm">
                        {product.expiry_status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-neutral-600 mt-0.5">
                      SKU: {product.product_sku} | Variant: {product.variant_id.substring(0, 12)}
                    </p>
                    {product.expired_quantity > 0 && (
                      <p className="text-xs text-error-600 mt-0.5">
                        {product.expired_quantity} expired (not available)
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <p
                      className={`text-sm font-semibold ${
                        product.total_quantity > 0 ? 'text-success-600' : 'text-error-600'
                      }`}
                    >
                      {product.total_quantity > 0
                        ? `${product.total_quantity} available`
                        : 'Out of stock'}
                    </p>
                    {product.expired_quantity > 0 && (
                      <p className="text-xs text-error-600 mt-0.5">
                        {product.expired_quantity} expired
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {showDropdown && searchQuery && filteredProducts.length === 0 && !isLoading && (
          <div className="absolute z-10 w-full mt-2 bg-white border border-neutral-200 rounded-lg shadow-lg p-4">
            <div className="text-center">
              <Icon name="Search" size={32} className="text-neutral-300 mx-auto mb-2" />
              <p className="text-neutral-600">No products found</p>
              <p className="text-sm text-neutral-500 mt-1">Try a different search term</p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Access Tiles */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Icon name="Zap" size={20} className="text-warning-600" />
          Quick Access
        </h3>
        {topProducts.length === 0 ? (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 text-center">
            <Icon name="Package" size={48} className="text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-600">No products available in this warehouse</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {topProducts.map((product) => (
              <button
                key={`${product.variant_id}-${product.warehouse_id}`}
                onClick={() => handleSelectProduct(product)}
                className="bg-white border-2 border-neutral-200 rounded-lg p-4 hover:border-primary-500 hover:shadow-md transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-2">
                  {/* Product Image */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    {product.images && product.images.length > 0 && imageUrls[product.variant_id] ? (
                      <img
                        src={imageUrls[product.variant_id]}
                        alt={product.product_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <Icon
                      name="Package"
                      size={24}
                      className={`text-primary-600 ${product.images && product.images.length > 0 && imageUrls[product.variant_id] ? 'hidden' : ''}`}
                    />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        product.total_quantity > 50
                          ? 'bg-success-100 text-success-700'
                          : product.total_quantity > 20
                            ? 'bg-warning-100 text-warning-700'
                            : 'bg-error-100 text-error-700'
                      }`}
                    >
                      {product.total_quantity} units
                    </span>
                    <Badge variant={getExpiryBadgeVariant(product.expiry_status) as any} size="sm">
                      {product.expiry_status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <h4 className="font-semibold text-neutral-900 mb-1 line-clamp-2 min-h-[2.5rem]">
                  {product.product_name}
                </h4>
                <p className="text-sm text-neutral-600 mb-2">
                  SKU: {product.product_sku || 'N/A'}
                </p>
                {product.expired_quantity > 0 && (
                  <p className="text-xs text-error-600 mb-1">
                    {product.expired_quantity} expired
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search Tips */}
      {!searchQuery && (
        <div className="bg-info-50 border border-info-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-info-600 mt-0.5" />
            <div>
              <p className="text-sm text-info-700">
                <strong>Tip:</strong> Start typing to search products, or click quick access tiles
                for top products
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
